import React, { useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useDrag } from 'react-dnd';
import { ChannelDatePopup } from './channel-date-popup';
import { savePlan, actions } from '../store/slice';

const ItemType = 'DRAGGABLE_ITEM';

const wrapperStyles = {
  border: '1px solid #171923',
  fontSize: '14px',
  fontWeight: 'bold',
  background: '#1a202',
  borderRadius: '6px',
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
};
const contentStyles = {
  backgroundColor: '#1a202c',
  color: '#fff',
  padding: '12px 14px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'move',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
};

export const BoxWithHandle = ({ data }) => {
  const dispatch = useDispatch();
  const { id, duration, title, description } = data;
  const [showPopup, setShowPopup] = useState(false);
  const dragElRef = useRef(null);

  const [{ opacity, isDragging }, drag, preview] = useDrag(() => ({
    type: ItemType,
    collect: monitor => ({
      opacity: monitor.isDragging() ? 0.5 : 1,
      isDragging: monitor.isDragging(),
    }),
    /** Pointer→pill-left offset at drag start only. Never use getClientOffset() here — it moves during drag and breaks anchorX. */
    item: monitor => {
      let sourceGrabOffsetX = 0;
      const pt = monitor.getInitialClientOffset?.() ?? monitor.getClientOffset?.();
      const el = dragElRef.current;
      if (pt && el) {
        const r = el.getBoundingClientRect();
        sourceGrabOffsetX = Math.max(0, Math.min(r.width, pt.x - r.left));
      }
      return {
        id,
        title,
        description,
        duration,
        isDraggableItem: true,
        sourceGrabOffsetX,
      };
    },
    end: (item, monitor) => {
      // Reset opacity when drag ends
      if (monitor.didDrop()) {
        // Item was successfully dropped
        console.log('Draggable item successfully dropped:', item);
      }
    },
  }));

  const handleClick = () => {
    setShowPopup(true);
  };

  const handleSave = scheduleData => {
    // scheduleData.startDate comes from a datetime-local input, e.g. "2025-02-19T10:30"
    // Extract date and time parts to build since/till in the existing format
    let since = '';
    let till = '';
    if (scheduleData.startDate) {
      const [datePart, timePartRaw] = scheduleData.startDate.split('T');
      const timePart = timePartRaw || '00:00';
      since = `${datePart}T${timePart}:00`;
      // Same calendar day end with explicit clock time (not T24) for API/SQL compatibility.
      till = `${datePart}T23:59:59`;
    }

    const payload = {
      title: id,
      channelUuid: scheduleData.channel,
      description: description,
      since,
      till,
    };

    dispatch(savePlan(payload));
    // Remove element from Task board after saving
    dispatch(actions.removeElement(id));
    setShowPopup(false);
  };

  const setDragRef = useCallback(
    node => {
      dragElRef.current = node;
      drag(node);
    },
    [drag],
  );

  return (
    <>
      <div 
        ref={setDragRef} 
        style={{ 
          ...wrapperStyles, 
          opacity,
          cursor: isDragging ? 'grabbing' : 'grab',
        }} 
        onClick={handleClick}
      >
        <div
          id={id}
          style={{
            ...contentStyles,
            pointerEvents: 'none', // Prevent interference with drag
          }}
          data-title={title}
          data-description={description}
          data-duration={duration}
        >
          {title} - {description}
        </div>
      </div>
      <ChannelDatePopup
        show={showPopup}
        onClose={() => setShowPopup(false)}
        onSave={handleSave}
        taskTitle={id}
        description={description}
      />
    </>
  );
};
